import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import {
  notificationsProvider,
  type Notification,
} from "@/providers/notifications.provider";

const WS_URL = `${process.env.BUN_PUBLIC_WS_BASE_URL}/v1/ws/connect`;
const MAX_BACKOFF_MS = 30_000;

function isNotification(msg: unknown): msg is Notification {
  return (
    typeof msg === "object" &&
    msg !== null &&
    "notification_id" in msg &&
    "body" in msg
  );
}

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  markRead: (id: string) => Promise<void>;
  markAllRead: (ids: string[]) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null,
);

export function NotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1_000);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmounted = useRef(false);
  const initialFetchDone = useRef(false);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const mergeNotifications = useCallback((incoming: Notification[]) => {
    setNotifications((prev) => {
      const map = new Map(prev.map((n) => [n.notification_id, n]));
      for (const n of incoming) {
        map.set(n.notification_id, n);
      }
      return Array.from(map.values()).sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    });
  }, []);

  const markRead = useCallback(async (id: string) => {
    await notificationsProvider.markRead(id);
    setNotifications((prev) =>
      prev.map((n) =>
        n.notification_id === id
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n,
      ),
    );
  }, []);

  const markAllRead = useCallback(async (ids: string[]) => {
    await Promise.allSettled(ids.map((id) => notificationsProvider.markRead(id)));
    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) =>
        ids.includes(n.notification_id)
          ? { ...n, is_read: true, read_at: now }
          : n,
      ),
    );
  }, []);

  function connect() {
    if (unmounted.current) return;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmounted.current) return ws.close();
      backoffRef.current = 1_000;
      initialFetchDone.current = false;
      notificationsProvider
        .list()
        .then((data) => {
          mergeNotifications(data);
          initialFetchDone.current = true;
        })
        .catch(() => {
          initialFetchDone.current = true;
        });
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (isNotification(msg)) {
          mergeNotifications([msg]);
          if (initialFetchDone.current) {
            toast(msg.body, { description: msg.action_type });
          }
        }
      } catch {}
    };

    ws.onclose = () => {
      if (unmounted.current) return;
      reconnectTimer.current = setTimeout(() => {
        backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
        connect();
      }, backoffRef.current);
    };

    ws.onerror = () => ws.close();
  }

  useEffect(() => {
    unmounted.current = false;
    connect();
    return () => {
      unmounted.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, []);

  return (
    <NotificationsContext.Provider
      value={{ notifications, unreadCount, markRead, markAllRead }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotificationsContext must be used inside NotificationsProvider",
    );
  }
  return ctx;
}
